import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  HeartIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Loading from '../components/ui/Loading';
import { useAppSelector } from '../redux/hooks';
import apiService from '../services/api';
import { Protocol } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAppSelector(state => state.user);
  const [stats, setStats] = useState({
    totalProtocols: 0,
    recentProtocols: 0,
    popularProtocols: 0,
    totalViews: 0,
  });
  const [recentProtocols, setRecentProtocols] = useState<Protocol[]>([]);
  const [popularProtocols, setPopularProtocols] = useState<Protocol[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const [recentResponse, popularResponse] = await Promise.all([
          apiService.getProtocols({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }),
          apiService.getProtocols({ limit: 5, sortBy: 'viewCount', sortOrder: 'desc' }),
        ]);

        setRecentProtocols(recentResponse.protocols);
        setPopularProtocols(popularResponse.protocols);

        // Calculate stats
        const totalViews = recentResponse.protocols.reduce((sum, p) => sum + (p.viewCount || 0), 0);
        setStats({
          totalProtocols: recentResponse.pagination.total,
          recentProtocols: recentResponse.protocols.length,
          popularProtocols: popularResponse.protocols.length,
          totalViews,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'medical':
        return <HeartIcon className='h-5 w-5 text-red-500' />;
      case 'surgical':
        return <DocumentTextIcon className='h-5 w-5 text-blue-500' />;
      case 'cardiac':
        return <HeartIcon className='h-5 w-5 text-red-600' />;
      case 'emergency':
        return <ArrowTrendingUpIcon className='h-5 w-5 text-yellow-500' />;
      case 'pediatric':
        return <UserGroupIcon className='h-5 w-5 text-green-500' />;
      case 'geriatric':
        return <ChartBarIcon className='h-5 w-5 text-purple-500' />;
      default:
        return <DocumentTextIcon className='h-5 w-5 text-gray-500' />;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      medical: 'Médico',
      surgical: 'Quirúrgico',
      cardiac: 'Cardíaco',
      emergency: 'Emergencia',
      pediatric: 'Pediatría',
      geriatric: 'Geriatría',
    };
    return labels[category] || category;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='flex justify-center items-center h-64'>
          <Loading size='lg' text='Cargando dashboard...' />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='space-y-6'>
        {/* Welcome Header */}
        <div className='bg-gradient-to-r from-insurance-600 to-insurance-800 rounded-lg p-6 text-white'>
          <h1 className='text-2xl font-bold mb-2'>¡Bienvenido, {user?.firstName}!</h1>
          <p className='text-insurance-100'>
            Aquí tienes un resumen de los protocolos médicos disponibles
          </p>
        </div>

        {/* Stats Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          <div className='card'>
            <div className='flex items-center'>
              <div className='p-3 bg-blue-100 rounded-lg'>
                <DocumentTextIcon className='h-6 w-6 text-blue-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>Total Protocolos</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.totalProtocols}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='flex items-center'>
              <div className='p-3 bg-green-100 rounded-lg'>
                <ClockIcon className='h-6 w-6 text-green-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>Recientes</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.recentProtocols}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='flex items-center'>
              <div className='p-3 bg-yellow-100 rounded-lg'>
                <ArrowTrendingUpIcon className='h-6 w-6 text-yellow-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>Populares</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.popularProtocols}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='flex items-center'>
              <div className='p-3 bg-purple-100 rounded-lg'>
                <EyeIcon className='h-6 w-6 text-purple-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-600'>Total Vistas</p>
                <p className='text-2xl font-bold text-gray-900'>{stats.totalViews}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Protocols */}
        <div className='card'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg font-semibold text-gray-900'>Protocolos Recientes</h2>
            <Link
              to='/protocols'
              className='text-insurance-600 hover:text-insurance-700 text-sm font-medium'
            >
              Ver todos
            </Link>
          </div>
          <div className='space-y-3'>
            {recentProtocols.map(protocol => (
              <Link
                key={protocol._id}
                to={`/protocols/${protocol._id}`}
                className='flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors'
              >
                <div className='flex-shrink-0 mr-3'>{getCategoryIcon(protocol.category)}</div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-gray-900 truncate'>{protocol.title}</p>
                  <p className='text-xs text-gray-500'>
                    {getCategoryLabel(protocol.category)} • v{protocol.version}
                  </p>
                </div>
                <div className='flex-shrink-0 text-xs text-gray-500'>
                  {protocol.viewCount} vistas
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Popular Protocols */}
        <div className='card'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg font-semibold text-gray-900'>Protocolos Populares</h2>
            <Link
              to='/search?sort=popular'
              className='text-insurance-600 hover:text-insurance-700 text-sm font-medium'
            >
              Ver todos
            </Link>
          </div>
          <div className='space-y-3'>
            {popularProtocols.map(protocol => (
              <Link
                key={protocol._id}
                to={`/protocols/${protocol._id}`}
                className='flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors'
              >
                <div className='flex-shrink-0 mr-3'>{getCategoryIcon(protocol.category)}</div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-gray-900 truncate'>{protocol.title}</p>
                  <p className='text-xs text-gray-500'>
                    {getCategoryLabel(protocol.category)} • v{protocol.version}
                  </p>
                </div>
                <div className='flex-shrink-0 text-xs text-gray-500'>
                  {protocol.viewCount} vistas
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Acciones Rápidas</h3>
            <div className='space-y-3'>
              <Link
                to='/search'
                className='flex items-center p-3 rounded-lg bg-insurance-50 hover:bg-insurance-100 transition-colors'
              >
                <DocumentTextIcon className='h-5 w-5 text-insurance-600 mr-3' />
                <span className='text-sm font-medium text-insurance-900'>Buscar Protocolos</span>
              </Link>
              <Link
                to='/protocols'
                className='flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors'
              >
                <ChartBarIcon className='h-5 w-5 text-gray-600 mr-3' />
                <span className='text-sm font-medium text-gray-900'>Ver Todos los Protocolos</span>
              </Link>
            </div>
          </div>

          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Categorías</h3>
            <div className='grid grid-cols-2 gap-2'>
              <Link
                to='/categories/medical'
                className='flex items-center p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors'
              >
                <HeartIcon className='h-4 w-4 text-red-600 mr-2' />
                <span className='text-xs font-medium text-red-900'>Médicos</span>
              </Link>
              <Link
                to='/categories/surgical'
                className='flex items-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors'
              >
                <DocumentTextIcon className='h-4 w-4 text-blue-600 mr-2' />
                <span className='text-xs font-medium text-blue-900'>Quirúrgicos</span>
              </Link>
              <Link
                to='/categories/emergency'
                className='flex items-center p-2 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors'
              >
                <ArrowTrendingUpIcon className='h-4 w-4 text-yellow-600 mr-2' />
                <span className='text-xs font-medium text-yellow-900'>Emergencias</span>
              </Link>
              <Link
                to='/categories/pediatric'
                className='flex items-center p-2 rounded-lg bg-green-50 hover:bg-green-100 transition-colors'
              >
                <UserGroupIcon className='h-4 w-4 text-green-600 mr-2' />
                <span className='text-xs font-medium text-green-900'>Pediatría</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
